/**
 * createCachedListDomain — 단순캐시 "리스트 도메인" 팩토리 (옵션2, ISSUE_167)
 *
 * classrooms·branches 에서 검증된 공통 골격을 추출. academy 스코프 리스트 + soft-delete + reorder
 * + realtime invalidate + Zustand 읽기미러 패턴을 한 곳에 모은다. 새 리스트 도메인은 config 만 작성.
 *
 * **변하는 것(config) = 4가지뿐**: 엔티티/입력 타입, queryKey, fetchFn+remote 함수, 스토어 setter.
 * **변하지 않는 것(팩토리) = 낙관 onMutate·롤백·invalidate·realtime 구독·미러 투영** (글자까지 동일).
 *
 * 데이터 접근을 이 한 겹으로 모음(forward-compat seam): 미래 PowerSync 부활 시 팩토리만 어댑터 교체.
 *
 * @module core/query/createCachedListDomain
 */

import { useEffect } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { queryClient as globalQueryClient } from "~/shared/lib/query-client";
import { useCurrentAcademyId } from "~/core/store/useWorkspaceStore";
import { useLiveSyncEnabled } from "~/core/store/sync/useSyncStore";
import { registerRealtimeChannel } from "~/core/realtime";

/** 리스트 도메인 엔티티 최소 형태. updatedAt/sortOrder 등은 도메인별(낙관에서 자동 주입 안 함). */
export interface ListEntity {
  id: string;
}

/** 워크스페이스 해석 결과 (academy 전용) */
export interface DomainWorkspace<FetchArg> {
  /** queryKey 분절 (academyId) */
  key: string;
  /** fetchFn 인자 */
  fetchArg: FetchArg;
  /** realtime 필터 value (null = 구독 안 함, 예: scratch) */
  realtimeValue: string | null;
  /** realtime 필터 컬럼 (기본 academy_id) */
  realtimeColumn?: string;
}

export interface CachedListConfig<T extends ListEntity, CreateInput, UpdateInput, FetchArg = string> {
  /** workspaceKey → queryKey */
  queryKey: (key: string) => readonly unknown[];
  /** 서버 읽기 */
  fetchFn: (arg: FetchArg) => Promise<{ data: T[]; error?: string }>;
  /** 순수 Supabase 쓰기. reorder 는 sort_order 보유 도메인만(옵션). */
  remote: {
    create: (input: CreateInput, id: string) => Promise<void>;
    update: (id: string, updates: UpdateInput) => Promise<void>;
    delete: (id: string) => Promise<void>;
    reorder?: (items: { id: string; sortOrder: number }[]) => Promise<void>;
  };
  /** CreateInput + id → 낙관 엔티티 */
  buildOptimistic: (input: CreateInput, id: string) => T;
  /** Zustand 미러 setter 를 주는 훅 (예: () => useXStore(s => s.setX)).
   *  isLoading 를 함께 받아 스토어 isLoading 을 query 상태와 동기화한다(전환 stale 방지). */
  useStoreSetter: () => (items: T[], isLoading?: boolean) => void;
  /** realtime 구독 테이블명 */
  realtimeTable: string;
  /** staleTime (캐시 강도) */
  staleTime: number;
  /** 워크스페이스 해석 훅 (생략 시 academy 전용 기본값 — classrooms/branches 류) */
  useWorkspaceResolver?: () => DomainWorkspace<FetchArg> | null;
}

type ReorderItems = { id: string; sortOrder: number }[];
type MutationCtx = { key: readonly unknown[]; prev: unknown } | undefined;

/** academy 전용 기본 resolver (useWorkspaceResolver 생략 시) */
function useDefaultAcademyWorkspace<FetchArg>(): DomainWorkspace<FetchArg> | null {
  const academyId = useCurrentAcademyId();
  if (!academyId) return null;
  return {
    key: academyId,
    fetchArg: academyId as unknown as FetchArg,
    realtimeValue: academyId,
    realtimeColumn: "academy_id",
  };
}

export interface CachedListDomain<T extends ListEntity, CreateInput, UpdateInput> {
  listKey: (key: string) => readonly unknown[];
  useList: () => ReturnType<typeof useQuery<T[]>>;
  useMirror: () => { items: T[]; isLoading: boolean };
  useCreate: () => UseMutationResult<T | null, Error, CreateInput>;
  useUpdate: () => UseMutationResult<void, Error, { id: string; updates: UpdateInput }, MutationCtx>;
  useDelete: () => UseMutationResult<void, Error, string, MutationCtx>;
  useReorder: () => UseMutationResult<void, Error, ReorderItems, MutationCtx>;
  invalidate: (key: string) => void;
}

export function createCachedListDomain<T extends ListEntity, CreateInput, UpdateInput, FetchArg = string>(
  config: CachedListConfig<T, CreateInput, UpdateInput, FetchArg>,
): CachedListDomain<T, CreateInput, UpdateInput> {
  const { queryKey, fetchFn, remote, buildOptimistic, useStoreSetter, realtimeTable, staleTime } = config;
  const useWorkspace: () => DomainWorkspace<FetchArg> | null =
    config.useWorkspaceResolver ?? (useDefaultAcademyWorkspace as () => DomainWorkspace<FetchArg> | null);

  function invalidate(key: string): void {
    void globalQueryClient.invalidateQueries({ queryKey: queryKey(key) });
  }

  // ── READ ──
  // ISSUE_167: 서버 SSOT — fetch는 게이트 무관 항상(미결제도 서버에서 읽음).
  // ISSUE_177: realtime(라이브 멀티에디터)만 liveSyncEnabled(프리미엄) 게이트(useMirror). 무료는 mount/refetch freshness.
  function useList() {
    const ws = useWorkspace();
    return useQuery<T[]>({
      queryKey: queryKey(ws?.key ?? "none"),
      enabled: !!ws,
      staleTime,
      queryFn: async () => {
        const res = await fetchFn(ws!.fetchArg);
        if (res.error) throw new Error(res.error);
        return res.data;
      },
    });
  }

  // ── 미러 투영 + realtime ──
  function useMirror() {
    const ws = useWorkspace();
    const liveSyncEnabled = useLiveSyncEnabled();
    const query = useList();
    const setItems = useStoreSetter();
    const data = query.data;
    const loading = query.isPending && query.fetchStatus !== "idle";

    // 워크스페이스 전환 시 새 키의 data 가 pending(undefined)이면 스토어를 즉시 비운다
    // (이전 학원 데이터 잔존=크로스테넌트 방지). isLoading 도 함께 밀어넣어, 비우는
    // 순간 store.isLoading 이 false 로 떨어져 EmptyState/폴백이 튀는 회귀를 막는다.
    useEffect(() => {
      setItems(data ?? [], loading);
    }, [data, loading, setItems]);

    const rtValue = ws?.realtimeValue ?? null;
    const rtColumn = ws?.realtimeColumn ?? "academy_id";
    const wsKey = ws?.key ?? null;
    useEffect(() => {
      if (!liveSyncEnabled || rtValue == null || wsKey == null) return;
      const unregister = registerRealtimeChannel({
        name: `${realtimeTable}:${rtColumn}=eq.${rtValue}`,
        event: "*",
        schema: "public",
        table: realtimeTable,
        filter: `${rtColumn}=eq.${rtValue}`,
        onPayload: () => invalidate(wsKey),
      });
      return () => unregister();
    }, [liveSyncEnabled, rtValue, rtColumn, wsKey]);

    return { items: data ?? [], isLoading: loading };
  }

  // ── WRITE: 생성 (낙관 append + 생성 엔티티 반환) ──
  function useCreate() {
    const qc = useQueryClient();
    const ws = useWorkspace();
    return useMutation<T | null, Error, CreateInput>({
      mutationFn: async (input: CreateInput): Promise<T | null> => {
        if (!ws) return null;
        const key = queryKey(ws.key);
        const id = crypto.randomUUID();
        const entity = buildOptimistic(input, id);
        qc.setQueryData<T[]>(key, (old) => [...(old ?? []), entity]);
        try {
          await remote.create(input, id);
        } catch (e) {
          qc.invalidateQueries({ queryKey: key });
          throw e;
        }
        return entity;
      },
    });
  }

  // ── WRITE: 수정 (낙관 머지) ──
  function useUpdate() {
    const qc = useQueryClient();
    const ws = useWorkspace();
    return useMutation<void, Error, { id: string; updates: UpdateInput }, MutationCtx>({
      mutationFn: async ({ id, updates }) => {
        await remote.update(id, updates);
      },
      onMutate: async ({ id, updates }) => {
        if (!ws) return undefined;
        const key = queryKey(ws.key);
        await qc.cancelQueries({ queryKey: key });
        const prev = qc.getQueryData<T[]>(key);
        qc.setQueryData<T[]>(key, (old) =>
          (old ?? []).map((item) =>
            item.id === id ? { ...item, ...(updates as Partial<T>) } : item,
          ),
        );
        return { key, prev };
      },
      onError: (_e, _v, ctx) => {
        if (ctx?.prev !== undefined) qc.setQueryData(ctx.key, ctx.prev);
      },
      onSettled: (_d, _e, _v, ctx) => {
        if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
      },
    });
  }

  // ── WRITE: 삭제 (낙관 제거 — soft delete) ──
  function useDelete() {
    const qc = useQueryClient();
    const ws = useWorkspace();
    return useMutation<void, Error, string, MutationCtx>({
      mutationFn: async (id: string) => {
        await remote.delete(id);
      },
      onMutate: async (id: string) => {
        if (!ws) return undefined;
        const key = queryKey(ws.key);
        await qc.cancelQueries({ queryKey: key });
        const prev = qc.getQueryData<T[]>(key);
        qc.setQueryData<T[]>(key, (old) => (old ?? []).filter((item) => item.id !== id));
        return { key, prev };
      },
      onError: (_e, _v, ctx) => {
        if (ctx?.prev !== undefined) qc.setQueryData(ctx.key, ctx.prev);
      },
      onSettled: (_d, _e, _v, ctx) => {
        if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
      },
    });
  }

  // ── WRITE: 순서 변경 (낙관 정렬) ──
  function useReorder() {
    const qc = useQueryClient();
    const ws = useWorkspace();
    return useMutation<void, Error, ReorderItems, MutationCtx>({
      mutationFn: async (items: ReorderItems) => {
        if (remote.reorder) await remote.reorder(items);
      },
      onMutate: async (items: ReorderItems) => {
        if (!ws) return undefined;
        const key = queryKey(ws.key);
        await qc.cancelQueries({ queryKey: key });
        const prev = qc.getQueryData<T[]>(key);
        const sortMap = new Map(items.map((i) => [i.id, i.sortOrder]));
        type Sorted = T & { sortOrder: number };
        qc.setQueryData<T[]>(key, (old) =>
          (old ?? [])
            .map((item) => (sortMap.has(item.id) ? { ...item, sortOrder: sortMap.get(item.id)! } : item))
            .sort((a, b) => (a as Sorted).sortOrder - (b as Sorted).sortOrder),
        );
        return { key, prev };
      },
      onError: (_e, _v, ctx) => {
        if (ctx?.prev !== undefined) qc.setQueryData(ctx.key, ctx.prev);
      },
      onSettled: (_d, _e, _v, ctx) => {
        if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
      },
    });
  }

  return { listKey: queryKey, useList, useMirror, useCreate, useUpdate, useDelete, useReorder, invalidate };
}
